import { useState } from 'react';
import { Button, Card } from '@lms/ui';
import { X, Sparkles } from 'lucide-react';
import { useBulkCreateSessions } from '../hooks/useLessonSessions';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';

interface BulkSessionModalProps {
  programId: string;
  subjectId: string;
  subjectName?: string;
  onClose: () => void;
}

export function BulkSessionModal({
  programId,
  subjectId,
  subjectName,
  onClose,
}: BulkSessionModalProps) {
  const { t } = useLanguage();
  const [count, setCount] = useState(20);
  const [titlePrefix, setTitlePrefix] = useState('Pertemuan');

  const bulkCreateMutation = useBulkCreateSessions();
  const isPending = bulkCreateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (count < 1) return;

    bulkCreateMutation.mutate(
      {
        programId,
        data: {
          subjectId,
          count: Number(count),
          titlePrefix: titlePrefix.trim() || 'Pertemuan',
        },
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-center p-4 bg-on-surface/20 backdrop-blur-sm animate-fadeIn overflow-y-auto py-12">
      <Card className="w-full max-w-md !p-0 overflow-hidden shadow-level5 border border-on-surface/5 animate-scaleIn my-auto">
        <div className="p-6 border-b border-outline-variant/10 flex justify-between items-center bg-surface-container-low">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center">
              <Sparkles size={20} />
            </div>
            <div>
              <h3 className="text-lg font-headline font-bold text-on-surface m-0">
                <Bilingual {...copy.bulkSessionModal.title} />
              </h3>
              <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-60">
                {subjectName || 'Mata Pelajaran'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-container-high rounded-full transition-colors text-on-surface-variant"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <p className="text-xs text-on-surface-variant leading-relaxed">
            <Bilingual {...copy.bulkSessionModal.subtitle} />
          </p>

          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="bulk-count"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.bulkSessionModal.labelCount} />{' '}
                <span className="text-error">*</span>
              </label>
              <input
                id="bulk-count"
                type="number"
                min={1}
                max={100}
                required
                value={count}
                onChange={e => setCount(Number(e.target.value))}
                className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-lg font-bold text-on-surface focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
              <p className="text-[10px] text-on-surface-variant ml-1">
                <Bilingual {...copy.bulkSessionModal.descCount} />
              </p>
            </div>

            <div className="flex gap-2">
              {[10, 20, 30, 40].map(val => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setCount(val)}
                  className={`flex-1 py-2 text-xs font-bold rounded-xl border transition-all ${
                    count === val
                      ? 'bg-primary text-on-primary border-primary'
                      : 'bg-surface-container-low text-on-surface border-outline-variant/20 hover:border-primary/40'
                  }`}
                >
                  +{val} Sesi
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="bulk-prefix"
                className="text-[10px] font-label font-black text-primary uppercase tracking-[0.2em] ml-1"
              >
                <Bilingual {...copy.bulkSessionModal.labelPrefix} />
              </label>
              <input
                id="bulk-prefix"
                type="text"
                value={titlePrefix}
                onChange={e => setTitlePrefix(e.target.value)}
                placeholder={t(copy.bulkSessionModal.placeholderPrefix)}
                className="w-full px-4 py-3 bg-surface-container-lowest border border-outline-variant/20 rounded-xl text-sm font-bold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-outline-variant/10">
            <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
              <Bilingual {...copy.levelModal.abort} />
            </Button>
            <Button variant="primary" type="submit" disabled={isPending || count < 1}>
              {isPending ? (
                <Bilingual {...copy.bulkSessionModal.btnSaving} />
              ) : (
                <Bilingual {...copy.bulkSessionModal.btnSubmit} />
              )}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
